import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    Card,
    CardBody,
    CardHeader,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledDropdown,
} from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

import {
    getRevenueByDayLastMonth,
    getRevenueByDayLastSevenDays,
    getRevenueByDayThisMonth,
    getRevenueByHourToday,
    getRevenueByHourYesterday,
} from "../../helpers/backend_helper";
import { toVND } from "../../helpers/helpers";
import { getPrimaryShopId } from "../../helpers/shop";

interface RevenueSummary {
    today: number;
    yesterday: number;
    thisMonth: number;
    lastMonth: number;
}

const extractReportRows = (response: any): any[] => {
    const direct = response?.data ?? response;
    return Array.isArray(direct) ? direct : [];
};

const sumTotals = (rows: any[]): number => {
    return rows.reduce((sum, row) => sum + (Number(row?.total ?? row?.revenue ?? 0) || 0), 0);
};

const formatDateLabel = (value: string): string => {
    if (!value) {
        return "--";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat("vi-VN", {
        month: "short",
        day: "2-digit",
    }).format(date);
};

const RevenueReport = () => {
    const authUser = useSelector((state: any) => state.auth?.user);
    const shopId = useMemo(() => getPrimaryShopId(authUser), [authUser]);

    const [summary, setSummary] = useState<RevenueSummary>({
        today: 0,
        yesterday: 0,
        thisMonth: 0,
        lastMonth: 0,
    });
    const [chartData, setChartData] = useState<{ x: string; y: number }[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!shopId) {
            setError("Không xác định được cửa hàng.");
            setSummary({ today: 0, yesterday: 0, thisMonth: 0, lastMonth: 0 });
            setChartData([]);
            return;
        }

        setLoading(true);
        setError(null);

        Promise.all([
            getRevenueByHourToday(shopId),
            getRevenueByHourYesterday(shopId),
            getRevenueByDayThisMonth(shopId),
            getRevenueByDayLastMonth(shopId),
            getRevenueByDayLastSevenDays(shopId),
        ])
            .then(([todayRes, yesterdayRes, thisMonthRes, lastMonthRes, chartRes]) => {
                const todayRows = extractReportRows(todayRes);
                const yesterdayRows = extractReportRows(yesterdayRes);
                const thisMonthRows = extractReportRows(thisMonthRes);
                const lastMonthRows = extractReportRows(lastMonthRes);
                const chartRows = extractReportRows(chartRes);

                setSummary({
                    today: sumTotals(todayRows),
                    yesterday: sumTotals(yesterdayRows),
                    thisMonth: sumTotals(thisMonthRows),
                    lastMonth: sumTotals(lastMonthRows),
                });

                setChartData(
                    chartRows.map((row) => ({
                        x: formatDateLabel(row?.date),
                        y: Number(row?.total ?? row?.revenue ?? 0) || 0,
                    }))
                );
            })
            .catch((err) => {
                const message = typeof err === "string" ? err : "Không thể tải báo cáo doanh thu.";
                setError(message);
                setChartData([]);
                setSummary({ today: 0, yesterday: 0, thisMonth: 0, lastMonth: 0 });
            })
            .finally(() => setLoading(false));
    }, [shopId]);

    const chartSeries = useMemo(
        () => [
            {
                name: "Doanh thu",
                data: chartData,
            },
        ],
        [chartData]
    );

    const chartOptions = useMemo<ApexOptions>(
        () => ({
            chart: {
                height: 350,
                type: "bar",
                toolbar: { show: false },
            },
            plotOptions: {
                bar: {
                    columnWidth: "50%",
                    dataLabels: { position: "top" },
                },
            },
            dataLabels: {
                enabled: true,
                style: {
                    fontSize: "12px",
                    colors: ["#adb5bd"],
                },
                offsetY: -20,
                formatter: (val: number) => toVND(val),
            },
            xaxis: {
                type: "category",
                labels: { rotate: -45 },
            },
            colors: ["#007bff"],
            legend: { show: false },
        }),
        []
    );

    const renderSummaryValue = (value: number) => {
        if (loading) {
            return "Đang tải...";
        }
        return toVND(value);
    };

    return (
        <React.Fragment>
            <div className="page-content-wrapper py-3">
                <div className="container">
                    {!shopId && (
                        <div className="alert alert-warning" role="alert">
                            Không tìm thấy cửa hàng mặc định. Vui lòng đăng nhập lại.
                        </div>
                    )}
                    {error && (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    )}
                    <div className="row g-2 rtl-flex-d-row-r">
                        <div className="col-6 col-md-4">
                            <div className="card">
                                <div className="card-body">Hôm nay : {renderSummaryValue(summary.today)}</div>
                            </div>
                        </div>
                        <div className="col-6 col-md-4">
                            <div className="card">
                                <div className="card-body">Hôm qua : {renderSummaryValue(summary.yesterday)}</div>
                            </div>
                        </div>
                        <div className="col-6 col-md-4">
                            <div className="card">
                                <div className="card-body">Tháng này : {renderSummaryValue(summary.thisMonth)}</div>
                            </div>
                        </div>
                        <div className="col-6 col-md-4">
                            <div className="card">
                                <div className="card-body">Tháng trước : {renderSummaryValue(summary.lastMonth)}</div>
                            </div>
                        </div>
                    </div>
                    <Card className="mt-2">
                        <CardHeader>
                            <div className="d-flex">
                                <h5 className="card-title mb-0 flex-grow-1  ">Doanh thu 7 ngày gần nhất</h5>
                                <div className="flex-shrink-0">
                                    <UncontrolledDropdown className="card-header-dropdown">
                                        <DropdownToggle className="text-reset dropdown-btn" tag="a" type="a" to="#">
                                            <span className="fw-semibold text-uppercase fs-12">Lọc theo: </span>
                                            <span className="text-muted">
                                                7 ngày
                                                <i className="mdi mdi-chevron-down ms-1"></i>
                                            </span>
                                        </DropdownToggle>
                                        <DropdownMenu className="dropdown-menu-end">
                                            <DropdownItem href="#">Hôm nay</DropdownItem>
                                            <DropdownItem href="#">Tuần này</DropdownItem>
                                            <DropdownItem href="#">Tuần trước</DropdownItem>
                                            <DropdownItem href="#">7 ngày qua</DropdownItem>
                                            <DropdownItem href="#">Tháng này</DropdownItem>
                                            <DropdownItem href="#">Tháng trước</DropdownItem>
                                        </DropdownMenu>
                                    </UncontrolledDropdown>
                                </div>
                            </div>
                        </CardHeader>
                        <CardBody>
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Đang tải...</span>
                                    </div>
                                </div>
                            ) : (
                                <ReactApexChart
                                    dir="ltr"
                                    className="apex-charts"
                                    series={chartSeries}
                                    options={chartOptions}
                                    type="bar"
                                    height={350}
                                />
                            )}
                        </CardBody>
                    </Card>
                </div>
            </div>
        </React.Fragment>
    );
};

export default RevenueReport;
